import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { openGoogleConnection } from "@/lib/googleOAuth";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FolderOpen, Folder, ChevronRight, Check, Loader2, RefreshCw, Plug } from "lucide-react";

function FieldRow({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

export default function GoogleDriveFields({ form, set, editId }) {
  const { t } = useTranslation();
  const [configured, setConfigured] = useState(true);
  const [connection, setConnection] = useState({ connected: false, email: null });
  const [connecting, setConnecting] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const authMethod = form.gdrive_auth_method || "oauth";

  const refreshConnection = useCallback(() => {
    api.get("/oauth/google/connection").then((r) => {
      setConnection(r.data.services?.drive || { connected: false, email: null });
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/oauth/google/config").then((r) => setConfigured(r.data.configured)).catch(() => setConfigured(false));
    refreshConnection();
  }, [refreshConnection]);

  const connect = async () => {
    setConnecting(true);
    try {
      const result = await openGoogleConnection(["drive"]);
      setConnection({ connected: true, email: result.email });
      set("gdrive_account_email")(result.email || "");
      toast.success(t("destinations.gdriveConnected"));
      refreshConnection();
    } catch (e) {
      toast.error(e.message || t("destinations.gdriveConnectFailed"));
    } finally { setConnecting(false); }
  };

  const canPick = authMethod === "service_account"
    ? !!form.gdrive_service_account_json
    : connection.connected;

  const openPicker = () => {
    if (authMethod === "oauth" && !connection.connected) { toast.error(t("destinations.gdriveConnectFirst")); return; }
    if (authMethod === "service_account" && !form.gdrive_service_account_json) { toast.error(t("destinations.gdriveSaJsonFirst")); return; }
    setPickerOpen(true);
  };

  return (
    <div className="space-y-4 border-l-2 border-brand/40 pl-3" data-testid="gdrive-fields">
      <FieldRow label={t("destinations.gdriveAuth")}>
        <Select value={authMethod} onValueChange={set("gdrive_auth_method")} disabled={!!editId}>
          <SelectTrigger className="rounded-none" data-testid="gdrive-auth-method"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="oauth">{t("destinations.gdriveOauth")}</SelectItem>
            <SelectItem value="service_account">{t("destinations.gdriveServiceAccount")}</SelectItem>
          </SelectContent>
        </Select>
      </FieldRow>

      {authMethod === "oauth" ? (
        <div className="space-y-2">
          {!configured && (
            <p className="font-mono text-[11px] text-destructive border border-destructive/40 bg-destructive/5 p-2">
              {t("destinations.gdriveNotConfigured")}
            </p>
          )}
          <div className="flex items-center justify-between border border-border bg-secondary px-3 py-2.5">
            <div className="min-w-0">
              {connection.connected ? (
                <p className="text-sm flex items-center gap-1.5 truncate" data-testid="gdrive-connection-status">
                  <Check className="h-3.5 w-3.5 text-success shrink-0" />
                  {t("destinations.gdriveConnectedAs", { email: connection.email })}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground" data-testid="gdrive-connection-status">{t("destinations.gdriveNotConnected")}</p>
              )}
            </div>
            <Button type="button" size="sm" variant="outline" className="rounded-none shrink-0"
              onClick={connect} disabled={connecting || !configured} data-testid="gdrive-connect-button">
              {connecting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : (connection.connected ? <RefreshCw className="h-3.5 w-3.5 mr-1" /> : <Plug className="h-3.5 w-3.5 mr-1" />)}
              {connecting ? t("destinations.gdriveConnecting") : (connection.connected ? t("destinations.gdriveReconnect") : t("destinations.gdriveConnect"))}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <FieldRow label={t("destinations.gdriveSaJson")}>
            <textarea data-testid="gdrive-sa-json" value={form.gdrive_service_account_json || ""}
              onChange={set("gdrive_service_account_json")} rows={4}
              className="w-full rounded-none border border-input bg-card p-2 font-mono text-[11px]"
              placeholder='{ "type": "service_account", "project_id": "…", "private_key": "…" }' />
          </FieldRow>
          <p className="font-mono text-[10px] text-muted-foreground">{t("destinations.gdriveSaHint")}</p>
        </>
      )}

      <FieldRow label={t("destinations.gdriveTargetFolder")}>
        <div className="flex items-center gap-2">
          <div className="flex-1 border border-input bg-card px-3 py-2 text-sm flex items-center gap-2 min-w-0" data-testid="gdrive-selected-folder">
            <FolderOpen className="h-4 w-4 text-brand shrink-0" />
            <span className="truncate">{form.gdrive_folder_name || form.gdrive_folder_id || <span className="text-muted-foreground">{t("destinations.gdriveNoFolder")}</span>}</span>
          </div>
          <Button type="button" variant="outline" className="rounded-none shrink-0" onClick={openPicker}
            disabled={!canPick} data-testid="gdrive-choose-folder">
            {form.gdrive_folder_id ? t("destinations.gdriveChangeFolder") : t("destinations.gdriveChooseFolder")}
          </Button>
        </div>
      </FieldRow>

      {pickerOpen && (
        <FolderPicker
          authMethod={authMethod}
          serviceAccountJson={form.gdrive_service_account_json}
          onClose={() => setPickerOpen(false)}
          onSelect={(folder) => {
            set("gdrive_folder_id")(folder.id);
            set("gdrive_folder_name")(folder.name);
            setPickerOpen(false);
          }}
        />
      )}
    </div>
  );
}

function FolderPicker({ authMethod, serviceAccountJson, onClose, onSelect }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [node, setNode] = useState({ parent_id: "root", parent_name: "", folders: [] });
  const [stack, setStack] = useState([]); // breadcrumb: [{id,name}]
  const reqRef = useRef(0);

  const browse = useCallback(async (parentId) => {
    const myReq = ++reqRef.current;
    setLoading(true);
    try {
      const r = await api.post("/oauth/drive/folders", {
        auth_method: authMethod,
        parent_id: parentId,
        service_account_json: authMethod === "service_account" ? serviceAccountJson : undefined,
      });
      if (myReq === reqRef.current) setNode(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("destinations.gdriveConnectFailed"));
    } finally {
      if (myReq === reqRef.current) setLoading(false);
    }
  }, [authMethod, serviceAccountJson, t]);

  useEffect(() => { browse("root"); }, [browse]);

  const enter = (f) => {
    setStack((s) => [...s, { id: node.parent_id, name: node.parent_name }]);
    browse(f.id);
  };
  const goTo = (idx) => {
    const target = stack[idx];
    setStack((s) => s.slice(0, idx));
    browse(target.id);
  };
  // At service-account root we list Shared Drives, which cannot themselves be the target
  // root selection (no usable My Drive); require navigating into one. Otherwise allow select.
  const canSelectCurrent = !(authMethod === "service_account" && node.parent_id === "root");

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-none max-w-md" data-testid="gdrive-folder-picker">
        <DialogHeader><DialogTitle className="font-display">{t("destinations.gdrivePickerTitle")}</DialogTitle></DialogHeader>

        <div className="flex items-center flex-wrap gap-1 text-xs font-mono text-muted-foreground">
          {stack.map((s, i) => (
            <span key={i} className="flex items-center gap-1">
              <button className="hover:text-brand" onClick={() => goTo(i)} data-testid={`gdrive-crumb-${i}`}>{s.name || "…"}</button>
              <ChevronRight className="h-3 w-3" />
            </span>
          ))}
          <span className="text-foreground">{node.parent_name || "…"}</span>
        </div>

        <div className="border border-border max-h-72 overflow-y-auto divide-y divide-border" data-testid="gdrive-folder-list">
          {loading ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> {t("destinations.gdriveLoadingFolders")}
            </div>
          ) : node.folders.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">{t("destinations.gdriveNoSubfolders")}</div>
          ) : node.folders.map((f) => (
            <button key={f.id} onClick={() => enter(f)} data-testid={`gdrive-folder-${f.id}`}
              className="w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-secondary text-left">
              <span className="flex items-center gap-2 min-w-0">
                <Folder className="h-4 w-4 text-brand shrink-0" />
                <span className="truncate">{f.name}</span>
              </span>
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
            </button>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>{t("common.cancel")}</Button>
          <Button className="rounded-none uppercase bg-[#0A0A0A] hover:bg-[#0052FF]"
            disabled={!canSelectCurrent}
            onClick={() => onSelect({ id: node.parent_id, name: node.parent_name })}
            data-testid="gdrive-select-folder">
            {t("destinations.gdriveSelectThis")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
